import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkle, Plus, ArrowRight, Check, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import { reconcileRazorpayOrder } from "../lib/razorpayReconcile";
import { useAuth } from "../context/AuthContext";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { inrFull } from "../lib/format";
import { FEATURE_LABELS } from "../lib/featureAccess";
import { formatTrialDuration } from "../lib/trialDuration";
import ThemeToggle from "../components/ThemeToggle";

function PlanBadge({ tier, status }) {
  const cls =
    tier === "pro"
      ? "bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))]"
      : tier === "basic"
        ? "bg-[hsl(var(--muted))] text-foreground"
        : "bg-[hsl(var(--muted))] text-muted-foreground";
  return (
    <span className={`text-[11px] uppercase tracking-wider px-2 py-1 ${cls}`} data-testid="profile-plan-badge">
      {tier}
      {status === "trial" ? " · trial" : ""}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function daysLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
}

export default function Profile() {
  const { user, refresh: refreshUser } = useAuth();
  const { access, refresh: refreshAccess } = useFeatureAccess();
  const [pricing, setPricing] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [confirmPro, setConfirmPro] = useState(null); // {target_plan, target_cycle, quote}
  const [reconciling, setReconciling] = useState(false);
  const handlerFiredRef = useRef(false);

  const loadPricing = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/pricing");
      setPricing(data);
    } catch {
      toast.error("Couldn't load pricing.");
    }
  }, []);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  const startCheckout = async (target_plan, target_cycle) => {
    setBusyPlan(`${target_plan}-${target_cycle}`);
    try {
      const Razorpay = await loadRazorpay();
      const { data: order } = await api.post("/billing/subscription/create-order", {
        target_plan, target_cycle,
      });
      handlerFiredRef.current = false;
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Estima",
        description: order.plan_label,
        order_id: order.order_id,
        prefill: { name: order.customer.name, email: order.customer.email },
        theme: { color: "#C85A32" },
        handler: async (resp) => {
          handlerFiredRef.current = true;
          try {
            await api.post("/billing/subscription/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`You're on ${order.plan_label}.`);
            await Promise.all([refreshUser(), refreshAccess()]);
          } catch (e) {
            // Signature verify failed (rare with QR) — fall back to reconcile
            const r = await reconcileRazorpayOrder("subscription", resp.razorpay_order_id);
            if (r.ok) {
              toast.success(`You're on ${order.plan_label}.`);
              await Promise.all([refreshUser(), refreshAccess()]);
            } else {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment verification failed.");
            }
          } finally {
            setBusyPlan(null);
          }
        },
        modal: {
          ondismiss: async () => {
            // QR / UPI flows often close the modal WITHOUT firing the handler
            // even though the payment was captured. Poll the reconcile endpoint
            // before giving up.
            if (handlerFiredRef.current) {
              setBusyPlan(null);
              return;
            }
            setReconciling(true);
            const r = await reconcileRazorpayOrder("subscription", order.order_id);
            setReconciling(false);
            setBusyPlan(null);
            if (r.ok) {
              toast.success(`Payment received — you're on ${order.plan_label}.`);
              await Promise.all([refreshUser(), refreshAccess()]);
            } else {
              toast("Checkout closed. If you completed payment, refresh in a minute.");
            }
          },
        },
      };
      new Razorpay(opts).open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusyPlan(null);
    }
  };

  // For an upgrade we first fetch the quote so we can show the differential + Pro warning.
  const handleUpgrade = async (target_plan, target_cycle) => {
    try {
      const { data: quote } = await api.post("/billing/upgrade-quote", { target_plan, target_cycle });
      if (quote.warning) {
        // Show modal-style confirm, then proceed
        setConfirmPro({ target_plan, target_cycle, quote });
        return;
      }
      await startCheckout(target_plan, target_cycle);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const lockedAddons = useMemo(
    () => (access?.all_addons_catalog || []).filter(
      (a) => !(access?.addons || []).includes(a.slug) && access?.tier === "basic"
    ),
    [access]
  );
  const myAddons = useMemo(
    () => (user?.addons || []).filter((a) => (access?.addons || []).includes(a.slug)),
    [user?.addons, access?.addons]
  );
  const includedFreeAddons = useMemo(
    () => (pricing?.addons || []).filter((a) => a.default_in_basic),
    [pricing?.addons]
  );

  if (!user || !pricing || !access) {
    return <div className="p-8 text-muted-foreground" data-testid="profile-loading">Loading…</div>;
  }

  const tier = access.tier;
  const cycle = access.plan_cycle;
  const status = access.plan_status;
  const expiresAt = access.plan_expires_at;
  const trialEndsAt = access.trial_ends_at;
  const isTrialing = status === "trial";
  const isPaid = status === "active" && (tier === "basic" || tier === "pro");
  const isBasicMonthly = tier === "basic" && cycle === "monthly";
  const isBasicYearly = tier === "basic" && cycle === "yearly";

  const upgradeOptions = [];
  if (isTrialing || tier === "free" || !isPaid) {
    // fresh purchase: all 4 options
    upgradeOptions.push(
      { plan: "basic", cycle: "monthly", price: pricing.basic.monthly, label: "Basic · Monthly", testid: "profile-buy-basic-monthly" },
      { plan: "basic", cycle: "yearly", price: pricing.basic.yearly, label: "Basic · Yearly", best: "Save 17%", testid: "profile-buy-basic-yearly" },
      { plan: "pro", cycle: "monthly", price: pricing.pro.monthly, label: "Pro · Monthly", testid: "profile-buy-pro-monthly" },
      { plan: "pro", cycle: "yearly", price: pricing.pro.yearly, label: "Pro · Yearly", best: "Save 17%", popular: true, testid: "profile-buy-pro-yearly" },
    );
  } else if (isBasicMonthly) {
    upgradeOptions.push(
      { plan: "basic", cycle: "yearly", label: "Basic · Yearly", testid: "profile-up-basic-yearly" },
      { plan: "pro", cycle: "monthly", label: "Pro · Monthly", testid: "profile-up-pro-monthly" },
      { plan: "pro", cycle: "yearly", label: "Pro · Yearly", popular: true, testid: "profile-up-pro-yearly" },
    );
  } else if (isBasicYearly) {
    upgradeOptions.push({ plan: "pro", cycle: "yearly", label: "Pro · Yearly", popular: true, testid: "profile-up-pro-yearly" });
  } else if (tier === "pro" && cycle === "monthly") {
    upgradeOptions.push({ plan: "pro", cycle: "yearly", label: "Pro · Yearly", popular: true, testid: "profile-up-pro-yearly" });
  }

  return (
    <div className="max-w-5xl mx-auto p-6 sm:p-8 space-y-8" data-testid="profile-page">
      {reconciling && (
        <div className="card-flat p-4 text-sm border-[hsl(var(--secondary))]" data-testid="profile-reconciling">
          <span className="text-[hsl(var(--secondary))]">Confirming your payment with Razorpay…</span>
          <span className="text-muted-foreground"> Hang tight, this can take up to 15 seconds for QR/UPI.</span>
        </div>
      )}
      <div>
        <div className="eyebrow mb-2">Account</div>
        <h1 className="font-serif text-4xl mb-2">Subscription & profile</h1>
        <p className="text-sm text-muted-foreground">
          {user.email} · <PlanBadge tier={tier} status={status} />
        </p>
      </div>

      {/* Appearance preference */}
      <div className="card-flat p-5 flex items-center justify-between gap-4" data-testid="profile-appearance">
        <div>
          <div className="eyebrow mb-1">Appearance</div>
          <div className="text-sm font-medium">Theme</div>
          <p className="text-xs text-muted-foreground mt-1">
            Choose between the warm-dark Editorial theme or a cream Light theme. Saved per browser.
          </p>
        </div>
        <ThemeToggle variant="row" testid="profile-theme-toggle" />
      </div>

      {/* Current plan */}
      <div className="card-flat p-6" data-testid="profile-current-plan">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="eyebrow mb-1">Current plan</div>
            <div className="font-serif text-3xl flex items-center gap-2">
              {tier === "pro" && <Crown size={22} weight="duotone" className="text-[hsl(var(--secondary))]" />}
              {tier === "free" ? "Free" : `${tier === "pro" ? "Pro" : "Basic"} · ${cycle ? cycle.charAt(0).toUpperCase() + cycle.slice(1) : "—"}`}
            </div>
          </div>
          <div className="text-right text-sm">
            {isTrialing && (
              <>
                <div className="text-[hsl(var(--secondary))]">Free trial</div>
                <div className="text-muted-foreground">{daysLeft(trialEndsAt)} days left · ends {fmtDate(trialEndsAt)}</div>
              </>
            )}
            {isPaid && (
              <>
                <div className="text-[hsl(var(--secondary))]">Active</div>
                <div className="text-muted-foreground">Renews / expires {fmtDate(expiresAt)}</div>
              </>
            )}
            {!isTrialing && !isPaid && tier === "free" && (
              <div className="text-muted-foreground">Pick a plan below to start.</div>
            )}
          </div>
        </div>

        {/* What you're paying for — itemised rate breakdown */}
        {(isPaid || isTrialing) && (
          <div className="border-t hairline pt-4 mt-4" data-testid="profile-features-rate-table">
            <div className="eyebrow mb-3">What you're paying for</div>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b hairline">
                  <th className="py-1.5 font-normal">Feature</th>
                  <th className="py-1.5 font-normal">Cycle</th>
                  <th className="py-1.5 font-normal text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hairline" data-testid="profile-rate-plan">
                  <td className="py-2">{tier === "pro" ? "Pro plan (everything unlocked)" : "Basic plan (Portfolio tracker + included features)"}</td>
                  <td className="py-2">{cycle || "trial"}</td>
                  <td className="py-2 text-right num-metric">
                    {isTrialing ? <span className="text-[hsl(var(--secondary))]">Free (trial)</span>
                      : `₹${(access.tier === "pro" ? pricing.pro[cycle] : pricing.basic[cycle]) || 0}`}
                  </td>
                </tr>
                {/* Add-ons included free with Basic */}
                {tier === "basic" && includedFreeAddons.map((a) => (
                  <tr key={a.slug} className="border-b hairline" data-testid={`profile-rate-addon-${a.slug}`}>
                    <td className="py-2">{a.name} <span className="text-[10px] text-[hsl(var(--secondary))] ml-1">Included free</span></td>
                    <td className="py-2">—</td>
                    <td className="py-2 text-right num-metric"><span className="text-[hsl(var(--secondary))]">₹0</span></td>
                  </tr>
                ))}
                {/* Paid add-ons from purchase history */}
                {myAddons.map((a) => {
                  const meta = (() => {
                    for (const cat of pricing.addons || []) {
                      if (cat.slug === a.slug) return cat;
                      const sub = (cat.sub_features || []).find((s) => s.slug === a.slug);
                      if (sub) return sub;
                    }
                    return null;
                  })();
                  return (
                    <tr key={a.slug} className="border-b hairline" data-testid={`profile-rate-paid-${a.slug}`}>
                      <td className="py-2">{meta?.name || a.slug}</td>
                      <td className="py-2">{a.cycle}</td>
                      <td className="py-2 text-right num-metric">{meta ? `₹${meta[a.cycle]}` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Active add-ons quick chips */}
        {myAddons.length > 0 && (
          <div className="border-t hairline pt-4 mt-4">
            <div className="eyebrow mb-2">Active add-ons</div>
            <ul className="space-y-1.5">
              {myAddons.map((a) => (
                <li key={a.slug} className="flex items-center justify-between text-sm" data-testid={`profile-addon-${a.slug}`}>
                  <span className="inline-flex items-center gap-2">
                    <Check size={14} weight="bold" className="text-[hsl(var(--secondary))]" />
                    {FEATURE_LABELS[a.slug] || a.slug}
                  </span>
                  <span className="text-muted-foreground">{a.cycle} · until {fmtDate(a.expires_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Upgrade / Buy options */}
      {upgradeOptions.length > 0 && (
        <div data-testid="profile-upgrades">
          <h2 className="font-serif text-2xl mb-1">{isPaid ? "Upgrade options" : "Choose your plan"}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {isPaid
              ? "When you upgrade, we deduct your current bill and you pay only the differential. Your time on the current plan is replaced by the new one (no double-billing)."
              : `Every account gets a ${formatTrialDuration(pricing.trial_days)} free trial with Basic features. Cancel anytime before.`}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {upgradeOptions.map((o) => (
              <UpgradeCard
                key={`${o.plan}-${o.cycle}`}
                option={o}
                pricing={pricing}
                isUpgrade={isPaid}
                busy={busyPlan === `${o.plan}-${o.cycle}`}
                onClick={() => (isPaid ? handleUpgrade(o.plan, o.cycle) : startCheckout(o.plan, o.cycle))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add-ons (Basic users only) */}
      {tier === "basic" && lockedAddons.length > 0 && (
        <div data-testid="profile-addons-marketplace">
          <h2 className="font-serif text-2xl mb-1">Add-ons</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Unlock individual Pro features without upgrading the whole plan. Or just go Pro and skip this.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {lockedAddons.map((a) => (
              <div key={a.slug} className="card-flat p-4" data-testid={`profile-addon-card-${a.slug}`}>
                <div className="text-sm font-medium mb-1">{a.name}</div>
                <div className="text-xs text-muted-foreground mb-3">Add this to your Basic plan.</div>
                <div className="flex gap-2">
                  <Link
                    to={`/app/${routeForFeature(a.slug)}`}
                    className="btn-ghost px-3 py-2 text-xs flex-1 text-center"
                    data-testid={`profile-addon-${a.slug}-go`}
                  >
                    {inrFull(a.monthly)}/mo · pay
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pro-confirm modal */}
      {confirmPro && (
        <div className="fixed inset-0 z-[10010] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="profile-pro-confirm">
          <div className="card-flat max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <Warning size={22} weight="duotone" className="text-[hsl(var(--secondary))] mt-0.5" />
              <div>
                <h3 className="font-serif text-2xl mb-1">Think it through</h3>
                <p className="text-sm text-muted-foreground">{confirmPro.quote.warning}</p>
              </div>
            </div>
            <div className="border-y hairline py-3 my-4 text-sm space-y-1.5">
              <Row k="New plan" v={`${confirmPro.target_plan.toUpperCase()} · ${confirmPro.target_cycle}`} />
              <Row k="Plan price" v={inrFull(confirmPro.quote.new_plan_price)} />
              <Row k="Deduction (current bill)" v={`− ${inrFull(confirmPro.quote.deduction)}`} />
              <Row k="Amount payable now" v={inrFull(confirmPro.quote.amount_payable)} bold />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmPro(null)} className="btn-ghost px-4 py-2 text-sm" data-testid="profile-pro-confirm-cancel">
                Not yet
              </button>
              <button
                onClick={async () => {
                  const { target_plan, target_cycle } = confirmPro;
                  setConfirmPro(null);
                  await startCheckout(target_plan, target_cycle);
                }}
                className="btn-primary px-4 py-2 text-sm"
                data-testid="profile-pro-confirm-go"
              >
                I understand · proceed <ArrowRight size={14} className="inline ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "text-foreground" : "text-muted-foreground"}`}>
      <span>{k}</span>
      <span className={bold ? "num-metric text-base" : ""}>{v}</span>
    </div>
  );
}

function UpgradeCard({ option, pricing, isUpgrade, busy, onClick }) {
  const price = pricing[option.plan][option.cycle];
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={option.testid}
      className={`card-flat p-5 text-left flex flex-col gap-2 transition hover:border-[hsl(var(--secondary))] disabled:opacity-50 ${option.popular ? "border-[hsl(var(--secondary))]" : ""}`}
    >
      {option.popular && (
        <div className="eyebrow text-[hsl(var(--secondary))] inline-flex items-center gap-1">
          <Sparkle size={11} weight="duotone" /> Recommended
        </div>
      )}
      <div className="font-serif text-xl">{option.label}</div>
      <div className="num-metric text-3xl">{inrFull(price)}</div>
      <div className="text-xs text-muted-foreground">{option.cycle === "monthly" ? "per month" : "per year, billed once"}</div>
      {option.best && <div className="text-[11px] text-[hsl(var(--secondary))]">{option.best}</div>}
      <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
        {isUpgrade ? "Upgrade" : "Subscribe"} <ArrowRight size={12} weight="bold" />
      </div>
    </button>
  );
}

function routeForFeature(slug) {
  const map = {
    "calculators": "calculators", "compare": "compare", "advisor": "advisor",
    "loan-rebalancer": "loans", "rent-comps": "rent-comps", "news": "news",
    "tenants": "tenants", "projects": "projects", "will": "will",
  };
  return map[slug] || "";
}
